import { FunctionComponent, useState } from "react"
import { Link } from "react-router-dom"
import { styled, colors, Avatar } from "@mui/material"
import { Box, Typography, Popover } from "@mui/material"
import { ArrowDropDown as ArrowDropDownIcon } from "@mui/icons-material"
import { Banner } from "@icanbwell/ui-toolkit"
import UserSVG from "assets/icon-user.svg"
import CloseSVG from "assets/icon-close.svg"
import ExitSVG from "assets/icon-exit.svg"
import ClientLogo from "components/ClientLogo"
import { useTranslation } from "react-i18next"
import Toolbar from "./Toolbar"
import { useAnalyticsTrackEvent } from "@client-hub/shared/react/components/AnalyticsProvider"
import { ACTION, APP_NAME } from "@client-hub/shared/constants/analytics"
import { useLogoSrcUrl } from "hooks"

const MenuButton = styled("button")(({ theme }) => ({
  position: "relative",
  maxWidth: "15rem",
  backgroundColor: theme.palette.common.primaryBase,
  padding: "1.15rem .625rem 1.15rem 4rem",
  textAlign: "left",
  cursor: "pointer",
  border: 0,
  marginLeft: "auto",
}))

const UserInfo = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",

  "& p": {
    color: theme.palette.common.white,
    fontWeight: 700,
  },

  "& p:nth-of-type(1)": {
    fontSize: "1.125rem",
    lineHeight: "1.35rem",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    maxWidth: "7.75rem",
  },

  "& p:nth-of-type(2)": {
    fontSize: "0.875rem",
    lineHeight: "1.35rem",
  },

  "& svg": {
    fontSize: "2.5rem",
    color: theme.palette.common.white,
  },
}))

const PopoverBodyWrapperStyled = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: ".25rem 1.25rem 1.625rem",

  "& p.user-role": {
    fontSize: "1rem",
    lineHeight: "1.25rem",
    marginBottom: ".25rem",
  },
})

const ProfilePopover = styled(Popover)({
  "& .banner-img": {
    maxHeight: "100%",
    width: "auto",
  },

  "& .banner-initials": {
    fontSize: "2rem",
  },

  ".custom-paper": {
    width: "25.875rem",
    borderBottomLeftRadius: "20%",
    borderBottomRightRadius: "20%",
  },
})

const ProfileLink = styled(Link)(({ theme }) => ({
  boxSizing: "border-box",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minWidth: "8rem",
  border: `2px solid ${theme.palette.common.primaryBase}`,
  padding: "0.095rem 0.5rem",
  cursor: "pointer",
  textDecoration: "none",

  "& svg": {
    marginRight: ".5rem",
    lineHeight: "2rem",
  },

  "& p": {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: theme.palette.common.primaryBase,
  },
}))

const CloseIconWrapper = styled("button")({
  position: "absolute",
  right: "2rem",
  top: "1.5rem",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "2rem",
  borderRadius: "50%",
  height: "2rem",
  background: "#fff",
  border: 0,
  zIndex: 1,
  cursor: "pointer",
})

const LogoutIconWrapper = styled("button")(({ theme }) => ({
  position: "absolute",
  left: "1rem",
  top: "1.5rem",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "6rem",
  height: "2rem",
  background: "transparent",
  border: 0,
  zIndex: 1,
  cursor: "pointer",
  color: theme.palette.common.primaryBase,

  "& svg": {
    marginRight: "5px",
  },
}))

export type Props = {
  user?: {
    profilePic?: string
    initials: string
    name: string
    isAdmin: boolean
  }
  onLogout: () => void
  myProfileLink?: string
  logoLink: string
}

const Header: FunctionComponent<Props> = ({ onLogout, user, myProfileLink, logoLink }) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const { trackEvent } = useAnalyticsTrackEvent()
  const logoSrc = useLogoSrcUrl()
  const handleClose = () => {
    setAnchorEl(null)
  }
  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleLogout = () => {
    trackEvent({
      appName: APP_NAME,
      action: ACTION.CLICKED_LOGOUT,
    })
    onLogout()
  }
  return (
    <Toolbar>
      {logoSrc && (
        <Link to={logoLink}>
          <ClientLogo height="2em" />
        </Link>
      )}
      {user && (
        <>
          <MenuButton onClick={handleOpen}>
            <Avatar
              sx={{
                position: "absolute",
                top: "1.25rem",
                left: "1rem",
                bgcolor: colors.deepPurple[600],
              }}
              src={user.profilePic}
              alt="profile img"
            >
              {user.initials}
            </Avatar>
            <UserInfo>
              <Box>
                <Typography>{user.name}</Typography>
                {user.isAdmin && <Typography>{t("header.adminUser").slice(0, 5)}</Typography>}
              </Box>
              <ArrowDropDownIcon />
            </UserInfo>
          </MenuButton>
          <ProfilePopover
            marginThreshold={0}
            id={anchorEl ? "profile-popover" : undefined}
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            PaperProps={{
              classes: {
                rounded: "custom-paper",
              },
            }}
          >
            <div>
              <CloseIconWrapper onClick={handleClose}>
                <CloseSVG height="1em" />
              </CloseIconWrapper>
              <LogoutIconWrapper onClick={handleLogout}>
                <ExitSVG />
                <Typography>{t("forms.credentials.logout")}</Typography>
              </LogoutIconWrapper>
              <Banner
                title={user.name}
                image={
                  <Avatar
                    sx={{
                      height: "100%",
                      width: "100%",
                      objectFit: "contain",
                      borderRadius: "50%",
                    }}
                    src={user.profilePic}
                    alt="profile img"
                  >
                    {user.initials}
                  </Avatar>
                }
                showRadialBg
              />
              <PopoverBodyWrapperStyled>
                {user.isAdmin && <Typography className="user-role">{t("header.adminUser")}</Typography>}
                {myProfileLink && (
                  <ProfileLink onClick={handleClose} to={myProfileLink}>
                    <UserSVG height="1em" />
                    <Typography>{t("routes.profile.profileTitle")}</Typography>
                  </ProfileLink>
                )}
              </PopoverBodyWrapperStyled>
            </div>
          </ProfilePopover>
        </>
      )}
    </Toolbar>
  )
}

export default Header
